import type { CSSProperties, ReactNode } from 'react';
import type { CountryId } from '@shared/types.ts';
import { COUNTRY_STYLE } from './CountryEmblem';
import { cx } from './cx';

export function initials(name: string): string {
  const parts = name.replace(/\./g, ' ').trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] ?? '?';
  const b = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (a + b).toUpperCase();
}

/** Blends two #rrggbb colors (t = share of `a`). Used instead of CSS color-mix for Safari 15. */
export function mixHex(a: string, b: string, t: number): string {
  const p = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const x = p(a);
  const y = p(b);
  return `#${x.map((v, i) => Math.round(v * t + (y[i] ?? 0) * (1 - t)).toString(16).padStart(2, '0')).join('')}`;
}

export interface AvatarProps {
  name: string;
  /** Tints the ring with the country's accent. */
  country?: CountryId | null;
  size?: number;
  absent?: boolean;
  /** Small overlay at the bottom right (a role icon, a readiness light). */
  badge?: ReactNode;
  className?: string;
}

/** Initials in a round seal, ringed with the country's color. Decorative: pair it with the name. */
export function Avatar({ name, country, size = 40, absent, badge, className }: AvatarProps) {
  const st = country ? COUNTRY_STYLE[country] : null;
  const style = {
    ['--avatar-size' as string]: `${size}px`,
    ['--avatar-ring' as string]: st?.accent,
    ['--avatar-bg' as string]: st ? mixHex(st.accent, '#172a4a', 0.2) : undefined,
  } as CSSProperties;
  return (
    <span className={cx('avatar', absent && 'avatar--absent', className)} style={style} aria-hidden>
      {initials(name)}
      {badge && <span className="avatar__badge">{badge}</span>}
    </span>
  );
}
